import { type ReactElement, useMemo } from 'react';
import type { RowData } from '@tanstack/react-table';
import { useCurrentUser } from '../../../hooks';
import { QText } from '../../../QAtoms';
import { CellProps } from './types';
import { getDataCy, getMetaField } from './meta';
import { formatDistanceToNow } from 'date-fns';

const tryToRenderRelativeDate = (value: number | Date) => {
  // we need to wrap formatDistanceToNow in a try catch
  // as it will raise an error if provided value is not a valid date
  try {
    return formatDistanceToNow(value, { addSuffix: true });
  } catch (err) {
    return undefined;
  }
};

/**
 * Displays a date in the user's selected Qualio-timezone.
 */
export function QDateCell<TData extends RowData>({
  getValue,
  column,
  row,
}: CellProps<TData, Date | undefined>): ReactElement {
  const { formatDate } = useCurrentUser();
  const { includeTime, highlightPastDates, useRelativeDate } = getMetaField(
    column,
    'date',
  );
  const value = getValue();

  const shouldhighlight = useMemo(() => {
    let shouldHighlightPastDates = highlightPastDates === true;

    if (typeof highlightPastDates === 'function') {
      shouldHighlightPastDates = highlightPastDates(row.original);
    }
    return shouldHighlightPastDates && value && new Date() > value;
  }, [value]);

  const renderedValue = value
    ? (useRelativeDate && tryToRenderRelativeDate(value)) ||
      formatDate(value, includeTime)
    : '-';

  return (
    <QText
      sx={{
        '&::first-letter': {
          textTransform: useRelativeDate ? 'uppercase' : undefined,
        },
      }}
      color={shouldhighlight ? 'red.500' : undefined}
      data-cy={getDataCy(column, row.original)}
    >
      {renderedValue}
    </QText>
  );
}
